import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown } from "tiptap-markdown";

/**
 * A normal document editor for blog posts: owners never see Markdown.
 *
 * Storage does not change: content is still Markdown, read and written through
 * tiptap-markdown, so the server renderer, WordPress, the hosted blog and the
 * agent all keep working on the same text. Images keep their `asset:<media_id>`
 * reference in the Markdown and are only shown through `resolveImage`.
 */

export interface BlogRichEditorHandle {
  /** `displayUrl` shows the picture right away (the library list may not have it yet). */
  insertImage: (src: string, alt: string, displayUrl?: string) => void;
}

interface BlogRichEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  /** asset:<media_id> → a displayable URL (else a labelled placeholder shows). */
  resolveImage: (src: string) => string;
  placeholder?: string;
}

// A block image that shows asset: images through the resolver without changing
// what is saved, and writes itself as its own Markdown block (the stock writer
// glues the next block onto the same line, which broke a following table).
export const BlogImage = Image.extend({
  addOptions() {
    return { ...this.parent?.(), inline: false, resolve: (s: string) => s } as any;
  },
  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any) {
          const esc = (t: string) => (t || "").replace(/[[\]]/g, "");
          const title = node.attrs.title ? ` "${String(node.attrs.title).replace(/"/g, "'")}"` : "";
          state.write(`![${esc(node.attrs.alt)}](${node.attrs.src}${title})`);
          state.closeBlock(node);
        },
        parse: {},
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const src = String(HTMLAttributes.src || "");
    const shown = (this.options as any).resolve(src) || "";
    return ["img", { ...HTMLAttributes, src: shown, "data-src": src, class: "blog-editor-img" }];
  },
});

const getMarkdown = (editor: Editor): string =>
  ((editor.storage as any).markdown?.getMarkdown?.() as string) ?? "";

const Btn: React.FC<{ label: string; active?: boolean; onClick: () => void; children: React.ReactNode }> = ({
  label, active, onClick, children,
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`flex h-8 min-w-[32px] items-center justify-center rounded-md px-1.5 text-sm ${
      active ? "bg-gray-200 text-gray-900" : "text-gray-600 hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);

const Icon: React.FC<{ name: string }> = ({ name }) => <span className="material-icons text-[18px]">{name}</span>;

export const BlogRichEditor = forwardRef<BlogRichEditorHandle, BlogRichEditorProps>(
  ({ value, onChange, resolveImage, placeholder = "Start writing your post…" }, ref) => {
    const lastEmitted = useRef<string>(value);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    // The image node is created once, so it must look up URLs through refs:
    // the latest resolver, plus URLs of images inserted in this session.
    const resolveRef = useRef(resolveImage);
    resolveRef.current = resolveImage;
    const insertedUrls = useRef<Record<string, string>>({});
    const resolveNow = useRef((src: string) => insertedUrls.current[src] || resolveRef.current(src));

    const editor = useEditor({
      extensions: [
        StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] } }),
        Link.configure({ openOnClick: false, autolink: true }),
        BlogImage.configure({ resolve: (src: string) => resolveNow.current(src), inline: false } as any),
        Table.configure({ resizable: false }),
        TableRow,
        TableHeader,
        TableCell,
        Placeholder.configure({ placeholder }),
        Markdown.configure({ html: true, tightLists: true, linkify: true, transformPastedText: true }),
      ],
      content: value,
      onUpdate: ({ editor: ed }) => {
        const md = getMarkdown(ed);
        lastEmitted.current = md;
        onChangeRef.current(md);
      },
      editorProps: {
        attributes: {
          class: "blog-rich-editor prose prose-sm max-w-none min-h-[320px] px-4 py-3 outline-none",
          "aria-label": "Blog post content",
        },
      },
    });

    // A different post (or the agent's new draft) replaces the document; our own
    // edits coming back through `value` do not.
    useEffect(() => {
      if (!editor || value === lastEmitted.current) return;
      lastEmitted.current = value;
      editor.commands.setContent(value, false);
    }, [editor, value]);

    useImperativeHandle(ref, () => ({
      insertImage: (src: string, alt: string, displayUrl?: string) => {
        if (displayUrl) insertedUrls.current[src] = displayUrl;
        editor?.chain().focus().setImage({ src, alt }).createParagraphNear().run();
      },
    }), [editor]);

    if (!editor) return null;

    const setLink = () => {
      const prev = editor.getAttributes("link").href as string | undefined;
      const url = window.prompt("Link address (leave empty to remove)", prev || "https://");
      if (url === null) return;
      if (!url.trim() || url.trim() === "https://") editor.chain().focus().unsetLink().run();
      else editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
    };

    return (
      <div className="rounded-lg border border-gray-200 bg-white">
        <style>{`
          .blog-rich-editor h2{font-size:1.25rem;font-weight:700;margin:1rem 0 .5rem}
          .blog-rich-editor h3{font-size:1.05rem;font-weight:700;margin:.85rem 0 .4rem}
          .blog-rich-editor p{margin:.5rem 0;line-height:1.65}
          .blog-rich-editor ul{list-style:disc;padding-left:1.4rem}
          .blog-rich-editor ol{list-style:decimal;padding-left:1.4rem}
          .blog-rich-editor blockquote{border-left:3px solid #d1d5db;padding-left:.8rem;color:#4b5563}
          .blog-rich-editor a{color:#2f4941;text-decoration:underline}
          .blog-rich-editor table{border-collapse:collapse;margin:.75rem 0;width:100%}
          .blog-rich-editor td,.blog-rich-editor th{border:1px solid #e5e7eb;padding:.35rem .5rem;text-align:left}
          .blog-rich-editor th{background:#f9fafb;font-weight:600}
          .blog-rich-editor img.blog-editor-img{max-width:100%;border-radius:8px;margin:.5rem 0;min-height:40px;background:#f3f4f6}
          .blog-rich-editor p.is-editor-empty:first-child::before{content:attr(data-placeholder);color:#9ca3af;float:left;height:0;pointer-events:none}
        `}</style>
        <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 px-2 py-1" role="toolbar" aria-label="Formatting">
          <Btn label="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><span className="text-xs font-bold">H2</span></Btn>
          <Btn label="Subheading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><span className="text-xs font-bold">H3</span></Btn>
          <Btn label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Icon name="format_bold" /></Btn>
          <Btn label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Icon name="format_italic" /></Btn>
          <Btn label="Bulleted list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><Icon name="format_list_bulleted" /></Btn>
          <Btn label="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><Icon name="format_list_numbered" /></Btn>
          <Btn label="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Icon name="format_quote" /></Btn>
          <Btn label="Link" active={editor.isActive("link")} onClick={setLink}><Icon name="link" /></Btn>
          <Btn label="Table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 2, withHeaderRow: true }).run()}><Icon name="table_chart" /></Btn>
          {editor.isActive("table") && (
            <>
              <Btn label="Add row" onClick={() => editor.chain().focus().addRowAfter().run()}><span className="text-xs">+row</span></Btn>
              <Btn label="Add column" onClick={() => editor.chain().focus().addColumnAfter().run()}><span className="text-xs">+col</span></Btn>
              <Btn label="Delete table" onClick={() => editor.chain().focus().deleteTable().run()}><span className="text-xs">×table</span></Btn>
            </>
          )}
          <span className="mx-1 h-5 w-px bg-gray-200" />
          <Btn label="Undo" onClick={() => editor.chain().focus().undo().run()}><Icon name="undo" /></Btn>
          <Btn label="Redo" onClick={() => editor.chain().focus().redo().run()}><Icon name="redo" /></Btn>
        </div>
        <EditorContent editor={editor} />
      </div>
    );
  }
);
BlogRichEditor.displayName = "BlogRichEditor";

export default BlogRichEditor;
